/**
 * Accounting Pricing Calculator
 *
 * Calculates pricing based on:
 * - Base service price (monthly or fixed)
 * - Revenue bracket addon
 * - Addon services (monthly, yearly, invoice-based, fixed)
 *
 * @package KTVHVN_Course_Registration
 * @since 1.1.0
 */

(function($) {
    'use strict';

    window.KTVHVN = window.KTVHVN || {};

    /**
     * Pricing Calculator
     */
    window.KTVHVN.AccountingPricing = class {

        constructor(config) {
            this.config = config;

            // Revenue bracket pricing (addon cost per month based on revenue)
            this.revenueBracketPricing = this.buildRevenuePricing(config);
        }

        /**
         * Calculate total price
         *
         * @param {Object} data
         * @return {Object}
         */
        calculate(data) {
            const {
                service_id,
                months = 3,
                invoices = 0,
                revenue_bracket = 'under_2',
                addons = [],
                products = 0,
                industry_id = ''
            } = data;

            // Get service with pricing_logic
            const service = this.getService(service_id);
            if (!service) {
                return this.emptyResult();
            }

            // Get pricing logic for this service
            const pricingLogic = service.pricing_logic || {};

            // Calculate base price using service-specific logic
            const basePrice = this.calculateBasePriceWithLogic(service, months, industry_id, pricingLogic);

            // Calculate revenue addon using service-specific logic
            const revenueAddon = this.calculateRevenueAddonWithLogic(service_id, revenue_bracket, months, pricingLogic);

            // Calculate addons total - only applicable addons for this service
            const applicableAddons = this.filterApplicableAddons(addons, pricingLogic);
            const addonsTotal = this.calculateAddonsTotal(applicableAddons, months, invoices);

            // Operational surcharges (HĐ/quý, SP/Hàng hóa) based on service pricing_logic
            const operational = this.calculateOperationalSurchargesWithLogic({
                months,
                invoices,
                products
            }, pricingLogic);

            // Total
            const total = basePrice + revenueAddon + addonsTotal + operational.total;

            return {
                base_price: basePrice,
                revenue_addon: revenueAddon,
                addons_total: addonsTotal,
                invoice_surcharge: operational.invoice_surcharge,
                product_surcharge: operational.product_surcharge,
                subtotal: total,  // FIX: Add subtotal field = total for displayPrice()
                total: total,
                breakdown: {
                    service: service,
                    months: months,
                    revenue_bracket: revenue_bracket,
                    addons: addons,
                    invoices: invoices,
                    products: products
                }
            };
        }

        /**
         * Calculate base service price (backward compatible wrapper)
         *
         * @param {Object} service
         * @param {Number} months
         * @return {Number}
         */
        calculateBasePrice(service, months, industryId = '') {
            return this.calculateBasePriceWithLogic(service, months, industryId, service.pricing_logic || {});
        }

        /**
         * Calculate base service price with pricing logic
         *
         * @param {Object} service
         * @param {Number} months
         * @param {String} industryId
         * @param {Object} pricingLogic
         * @return {Number}
         */
        calculateBasePriceWithLogic(service, months, industryId, pricingLogic) {
            if (!service) {
                return 0;
            }

            let basePrice = parseFloat(service.base_price) || 0;

            // Check if should use industry min price based on pricing_logic
            const useIndustryMin = pricingLogic.use_industry_min_price !== undefined
                ? pricingLogic.use_industry_min_price
                : (basePrice <= 0); // Fallback to old behavior

            if (useIndustryMin && industryId) {
                // Use per-service industry_min_prices if available
                const industryMin = this.getServiceIndustryMinPrice(industryId, pricingLogic);
                if (industryMin > 0) {
                    basePrice = industryMin;
                }
            }

            // If no base price found, use service base_price (even if 0)
            if (basePrice <= 0) {
                basePrice = parseFloat(service.base_price) || 0;
            }

            switch (service.unit) {
                case 'month':
                    return basePrice * months;

                case 'quarter':
                    return basePrice * Math.ceil(months / 3);

                case 'year':
                    return basePrice * Math.ceil(months / 12);

                case 'fixed':
                default:
                    return basePrice;
            }
        }

        /**
         * Get industry min price for a specific service (per-service first, then global fallback)
         *
         * @param {String} industryId
         * @param {Object} pricingLogic
         * @return {Number}
         */
        getServiceIndustryMinPrice(industryId, pricingLogic) {
            if (!industryId) {
                console.log('[DEBUG getServiceIndustryMinPrice] No industryId provided');
                return 0;
            }

            console.log('[DEBUG getServiceIndustryMinPrice] Input:', {
                industryId: industryId,
                pricingLogic: pricingLogic,
                hasIndustryMinPrices: !!pricingLogic.industry_min_prices,
                industryMinPrices: pricingLogic.industry_min_prices
            });

            // Check per-service industry_min_prices first
            if (pricingLogic.industry_min_prices && typeof pricingLogic.industry_min_prices === 'object') {
                if (pricingLogic.industry_min_prices[industryId] !== undefined) {
                    const price = parseFloat(pricingLogic.industry_min_prices[industryId]) || 0;
                    console.log('[DEBUG getServiceIndustryMinPrice] Using per-service price:', price);
                    return price;
                }
            }

            // Fallback to global industries
            const globalPrice = this.getIndustryMinPrice(industryId);
            console.log('[DEBUG getServiceIndustryMinPrice] Using global price:', globalPrice);
            return globalPrice;
        }

        /**
         * Calculate revenue bracket addon (backward compatible)
         *
         * Only applies to monthly services
         *
         * @param {String} revenueBracket
         * @param {Number} months
         * @return {Number}
         */
        calculateRevenueAddon(revenueBracket, months) {
            if (!revenueBracket || !this.revenueBracketPricing[revenueBracket]) {
                return 0;
            }

            const addonPerMonth = this.revenueBracketPricing[revenueBracket];
            return addonPerMonth * months;
        }

        /**
         * Calculate revenue addon with service-specific pricing logic
         *
         * @param {String} serviceId
         * @param {String} revenueBracket
         * @param {Number} months
         * @param {Object} pricingLogic
         * @return {Number}
         */
        calculateRevenueAddonWithLogic(serviceId, revenueBracket, months, pricingLogic) {
            // Check if service has custom_revenue_tiers (new format)
            const customTiersRaw = pricingLogic.custom_revenue_tiers;
            const customTiers = Array.isArray(customTiersRaw)
                ? customTiersRaw
                : (customTiersRaw && typeof customTiersRaw === 'object' ? Object.values(customTiersRaw) : null);
            if (Array.isArray(customTiers) && customTiers.length > 0) {
                return this.calculateRevenueAddonWithCustomTiers(revenueBracket, months, customTiers);
            }

            // Check if service has revenue addon disabled
            if (!pricingLogic.use_global_revenue_brackets && !pricingLogic.revenue_addon_table) {
                return 0;
            }

            const bracketKey = revenueBracket || 'under_2';
            let addonPerMonth = 0;

            // Use service-specific revenue table if available
            if (pricingLogic.revenue_addon_table && typeof pricingLogic.revenue_addon_table === 'object') {
                addonPerMonth = parseFloat(pricingLogic.revenue_addon_table[bracketKey]) || 0;
            } else if (pricingLogic.use_global_revenue_brackets) {
                // Use global revenue brackets
                addonPerMonth = this.revenueBracketPricing[bracketKey] || 0;
            }

            return addonPerMonth * months;
        }

        /**
         * Calculate revenue addon using custom_revenue_tiers
         *
         * @param {String} revenueBracket - e.g., 'custom_0', 'custom_1', etc.
         * @param {Number} months
         * @param {Array} customTiers - array of {from, to, label, addon}
         * @return {Number}
         */
        calculateRevenueAddonWithCustomTiers(revenueBracket, months, customTiers) {
            if (!revenueBracket || !Array.isArray(customTiers) || customTiers.length === 0) {
                return 0;
            }

            // Extract tier index from bracket key (e.g., 'custom_0' -> 0)
            let tierIndex = 0;
            if (typeof revenueBracket === 'string' && revenueBracket.startsWith('custom_')) {
                tierIndex = parseInt(revenueBracket.replace('custom_', ''), 10) || 0;
            }

            // Get the tier at the specified index
            const tier = customTiers[tierIndex];
            if (!tier) {
                return 0;
            }

            const addonPerMonth = parseFloat(tier.addon) || 0;
            return addonPerMonth * months;
        }

        /**
         * Get custom revenue tiers for a specific service
         *
         * @param {Object} service
         * @return {Array|null}
         */
        getServiceCustomRevenueTiers(service) {
            if (!service || !service.pricing_logic) {
                return null;
            }
            const tiers = service.pricing_logic.custom_revenue_tiers;
            if (Array.isArray(tiers)) {
                return tiers;
            }
            if (tiers && typeof tiers === 'object') {
                return Object.values(tiers);
            }
            return null;
        }

        /**
         * Check if an industry is enabled for a specific service
         *
         * @param {Object} service
         * @param {String} industryId
         * @return {Boolean}
         */
        isIndustryEnabledForService(service, industryId) {
            if (!service || !service.pricing_logic) {
                return true; // No pricing_logic = all enabled
            }

            const enabled = service.pricing_logic.enabled_industries;

            // null = all enabled
            if (enabled === null || enabled === undefined) {
                return true;
            }

            // Array = check if industry is in the list
            if (Array.isArray(enabled)) {
                return enabled.includes(industryId);
            }

            return true;
        }

        /**
         * Get enabled industries for a specific service
         *
         * @param {Object} service
         * @return {Array|null} null = all enabled, array = specific IDs
         */
        getServiceEnabledIndustries(service) {
            if (!service || !service.pricing_logic) {
                return null; // All enabled
            }
            return service.pricing_logic.enabled_industries || null;
        }

        /**
         * Calculate operational surcharges with pricing logic
         *
         * @param {Object} params
         * @param {Object} pricingLogic
         * @return {Object}
         */
        calculateOperationalSurchargesWithLogic({ months = 0, invoices = 0, products = 0 }, pricingLogic) {
            const globalRules = this.config?.pricing_rules || {};
            let invoiceSurcharge = 0;
            let productSurcharge = 0;

            // Invoice surcharge - only if enabled for this service
            const useInvoice = pricingLogic.use_invoice_surcharge !== undefined
                ? pricingLogic.use_invoice_surcharge
                : true; // Default enabled for backward compatibility

            if (useInvoice) {
                // Use per-service invoice_rate if available, else global
                const invoiceRate = pricingLogic.invoice_rate !== undefined
                    ? parseFloat(pricingLogic.invoice_rate)
                    : (parseFloat(globalRules.invoice_rate) || 0);
                const invoicesPerMonth = (parseFloat(invoices) || 0) / 3;
                invoiceSurcharge = invoiceRate * invoicesPerMonth * months;
            }

            // Product surcharge - only if enabled for this service
            const useProduct = pricingLogic.use_product_surcharge !== undefined
                ? pricingLogic.use_product_surcharge
                : true; // Default enabled for backward compatibility

            if (useProduct) {
                const productRate = parseFloat(globalRules.product_rate) || 0;
                if (productRate > 0) {
                    productSurcharge = productRate * (parseFloat(products) || 0) * months;
                } else {
                    // Use per-service product_tiers if available
                    productSurcharge = this.calculateProductTierSurchargeWithLogic(products, months, pricingLogic);
                }
            }

            return {
                invoice_surcharge: invoiceSurcharge,
                product_surcharge: productSurcharge,
                total: invoiceSurcharge + productSurcharge
            };
        }

        /**
         * Calculate product tier surcharge with per-service tiers
         *
         * @param {Number} products
         * @param {Number} months
         * @param {Object} pricingLogic
         * @returns {Number}
         */
        calculateProductTierSurchargeWithLogic(products, months, pricingLogic) {
            const count = parseFloat(products) || 0;

            // Use per-service product_tiers if available
            const serviceTiers = pricingLogic.product_tiers;
            const globalTiers = this.config?.pricing_rules?.product_tiers;

            const fallback = [
                { min: 30, max: 50, amount: 1500000 },
                { min: 51, max: 200, amount: 2500000 },
                { min: 201, max: 500, amount: 3500000 },
                { min: 501, max: Infinity, amount: 5000000 },
            ];

            // Priority: service tiers > global tiers > fallback
            let tiers = fallback;
            if (Array.isArray(serviceTiers) && serviceTiers.length > 0) {
                tiers = serviceTiers;
            } else if (Array.isArray(globalTiers) && globalTiers.length > 0) {
                tiers = globalTiers;
            }

            for (const tier of tiers) {
                const min = typeof tier.min === 'number' ? tier.min : 0;
                const max = tier.max === null || tier.max === undefined ? Infinity : parseFloat(tier.max);
                const amount = parseFloat(tier.amount) || 0;

                if (count >= min && count <= max) {
                    return amount * months;
                }
            }

            return 0;
        }

        /**
         * Filter addons to only include applicable ones for this service
         *
         * @param {Array} addons
         * @param {Object} pricingLogic
         * @return {Array}
         */
        filterApplicableAddons(addons, pricingLogic) {
            if (!addons || addons.length === 0) {
                return [];
            }

            const applicableAddonIds = pricingLogic.applicable_addons;

            // If 'all' or undefined, return all addons
            if (!applicableAddonIds || (Array.isArray(applicableAddonIds) && applicableAddonIds.includes('all'))) {
                return addons;
            }

            // If empty array, return no addons
            if (Array.isArray(applicableAddonIds) && applicableAddonIds.length === 0) {
                return [];
            }

            // Filter to only include applicable addons
            return addons.filter(addon => {
                const addonId = typeof addon === 'object' ? addon.id : addon;
                return applicableAddonIds.includes(addonId);
            });
        }

        /**
         * Build revenue pricing map from config with safe defaults
         *
         * @param {Object} config
         * @return {Object}
         */
        buildRevenuePricing(config) {
            const defaults = {
                'under_2': 0,
                '2_5': 200000,
                '5_10': 400000,
                '10_15': 600000,
                '15_25': 800000,
                '25_50': 1000000,
                'over_50': 1500000,
                // Legacy IDs
                '2_to_5': 200000,
                '5_to_10': 400000,
                '10_to_20': 600000,
                '20_to_50': 800000,
                '50_to_100': 1000000,
                'over_100': 1500000
            };

            if (!config || !config.revenue_brackets) {
                return defaults;
            }

            const pricing = {};
            config.revenue_brackets.forEach(bracket => {
                const id = bracket.id;
                const addon = parseFloat(bracket.addon_per_month) || defaults[id] || 0;
                pricing[id] = addon;
            });

            return { ...defaults, ...pricing };
        }

        /**
         * Calculate total for addon services
         *
         * @param {Array} addons
         * @param {Number} months
         * @param {Number} invoices
         * @return {Number}
         */
        calculateAddonsTotal(addons, months, invoices) {
            if (!addons || addons.length === 0) {
                return 0;
            }

            let total = 0;

            addons.forEach(addon => {
                const price = parseFloat(addon.price) || 0;
                const unit = addon.unit;

                switch (unit) {
                    case 'month':
                        total += price * months;
                        break;

                    case 'quarter':
                        total += price * Math.ceil(months / 3);
                        break;

                    case 'year':
                        total += price;
                        break;

                    case 'invoice':
                        total += price * invoices;
                        break;

                    case 'time':
                    case 'fixed':
                    default:
                        total += price;
                        break;
                }
            });

            return total;
        }

        /**
         * Calculate simple operational surcharges (ported from WPForm 34676)
         *
         * - Invoice surcharge: (invoices per quarter / 3) * invoice_rate * months
         * - Product surcharge: products * product_rate * months
         */
        calculateOperationalSurcharges({ months = 0, invoices = 0, products = 0 }) {
            const rules = this.config?.pricing_rules || {};
            const invoiceRate = parseFloat(rules.invoice_rate) || 0;
            const productRate = parseFloat(rules.product_rate) || 0;

            // Hóa đơn/quý -> tính theo tháng
            const invoicesPerMonth = (parseFloat(invoices) || 0) / 3;
            const invoiceSurcharge = invoiceRate * invoicesPerMonth * months;

            // Sản phẩm/hàng hóa theo tháng
            let productSurcharge = 0;
            if (productRate > 0) {
                productSurcharge = productRate * (parseFloat(products) || 0) * months;
            } else {
                productSurcharge = this.calculateProductTierSurcharge(products, months);
            }

            return {
                invoice_surcharge: invoiceSurcharge,
                product_surcharge: productSurcharge,
                total: invoiceSurcharge + productSurcharge
            };
        }

        /**
         * Get industry min price from config
         *
         * @param {String} industryId
         * @return {Number}
         */
        getIndustryMinPrice(industryId) {
            if (!industryId || !this.config?.industries) {
                return 0;
            }
            const industry = this.config.industries.find((i) => i.id === industryId);
            return industry && industry.min_price ? parseFloat(industry.min_price) : 0;
        }

        /**
         * Calculate product surcharge using tier config when product_rate = 0
         *
         * @param {Number} products
         * @param {Number} months
         * @returns {Number}
         */
        calculateProductTierSurcharge(products, months) {
            const tiers = this.config?.pricing_rules?.product_tiers;
            const count = parseFloat(products) || 0;
            const fallback = [
                { min: 30, max: 50, amount: 1500000 },
                { min: 51, max: 200, amount: 2500000 },
                { min: 201, max: 500, amount: 3500000 },
                { min: 501, max: Infinity, amount: 5000000 },
            ];

            const list = Array.isArray(tiers) && tiers.length ? tiers : fallback;

            for (const tier of list) {
                const min = typeof tier.min === 'number' ? tier.min : 0;
                const max = typeof tier.max === 'number' ? tier.max : Infinity;
                const amount = parseFloat(tier.amount) || 0;

                if (count >= min && count <= max) {
                    return amount * months;
                }
            }

            return 0;
        }

        /**
         * Get service by ID
         *
         * @param {String} serviceId
         * @return {Object|null}
         */
        getService(serviceId) {
            if (!this.config || !this.config.services) {
                return null;
            }

            const service = this.config.services.find(s => s.id === serviceId);
            return service || null;
        }

        /**
         * Empty result
         *
         * @return {Object}
         */
        emptyResult() {
            return {
                base_price: 0,
                revenue_addon: 0,
                addons_total: 0,
                subtotal: 0,  // FIX: Add subtotal field for displayPrice()
                total: 0,
                breakdown: {}
            };
        }

        /**
         * Format price for display
         *
         * @param {Number} amount
         * @return {String}
         */
        formatPrice(amount) {
            return new Intl.NumberFormat('vi-VN', {
                style: 'currency',
                currency: 'VND'
            }).format(amount);
        }

        /**
         * Get revenue bracket label
         *
         * @param {String} bracketId
         * @return {String}
         */
        getRevenueBracketLabel(bracketId) {
            if (!this.config || !this.config.revenue_brackets) {
                return '';
            }

            const bracket = this.config.revenue_brackets.find(b => b.id === bracketId);
            return bracket ? bracket.label : '';
        }

        /**
         * Calculate savings if user selects 12 months vs shorter period
         *
         * @param {Object} data
         * @return {Object}
         */
        calculateSavings(data) {
            // Calculate for current months
            const current = this.calculate(data);

            // Calculate for 12 months
            const yearlyData = { ...data, months: 12 };
            const yearly = this.calculate(yearlyData);

            // Average monthly cost
            const currentMonthly = data.months > 0 ? current.total / data.months : 0;
            const yearlyMonthly = yearly.total / 12;

            return {
                current_total: current.total,
                yearly_total: yearly.total,
                current_monthly: currentMonthly,
                yearly_monthly: yearlyMonthly,
                savings: (currentMonthly - yearlyMonthly) * data.months,
                savings_percentage: currentMonthly > 0
                    ? ((currentMonthly - yearlyMonthly) / currentMonthly * 100)
                    : 0
            };
        }
    };

})(jQuery);
